import React, { useState, useEffect, useMemo } from "react";
import {
  Container,
  Row,
  Col,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormGroup,
  Label,
  Input,
  Spinner,
} from "reactstrap";
import {
  getSubmissionListById,
  submitJudgeScores,
  getJudgeChallenge,
  getChallengeResults,
} from "../../api/ApiServices";

/* ─── helpers ─────────────────────────────────────────────────── */
const STATUS_CFG = {
  winner: { label: "Winner", bg: "#d1fae5", color: "#56e80d" },
  reviewed: { label: "Reviewed", bg: "#d1fae5", color: "#065f46" },
  declared: { label: "Declared", bg: "#e0e7ff", color: "#3730a3" },
  rejected: { label: "Rejected", bg: "#fee2e2", color: "#991b1b" },
  pending: { label: "Pending", bg: "#fef3c7", color: "#92400e" },
};
const isReviewed = (s) =>
  ["winner", "reviewed", "declared"].includes(s.rawStatus);

const Pill = ({ raw }) => {
  const c = STATUS_CFG[raw] || STATUS_CFG.pending;
  return (
    <span
      style={{
        background: c.bg,
        color: c.color,
        borderRadius: 999,
        fontSize: "0.72rem",
        fontWeight: 700,
        padding: "3px 12px",
        letterSpacing: "0.4px",
        whiteSpace: "nowrap",
      }}
    >
      {c.label}
    </span>
  );
};

const ITEMS_PER_PAGE = 5;

/* ─── component ───────────────────────────────────────────────── */
const JudgesDashboard = () => {
  const [allSubs, setAllSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState(null);
  const [modal, setModal] = useState(false);
  const [toast, setToast] = useState(false);
  const [selected, setSelected] = useState(null);
  const [scores, setScores] = useState({
    innovation: 0,
    technical: 0,
    uiux: 0,
    impact: 0,
  });
  const [comments, setComments] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [stats, setStats] = useState({ total: 0, reviewed: 0, pending: 0 });
  const [challengeId, setChallengeId] = useState(null);
  const [challengeData, setChallengeData] = useState(null);

  const [challengesList, setChallengesList] = useState([]);
  const [fetchError, setFetchError] = useState(null);
  const [submitError, setSubmitError] = useState("");

  // search + filter + pagination
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(1);

  // On mount: discover which challenge this judge is assigned to
  useEffect(() => {
    const init = async () => {
      try {
        const res = await getJudgeChallenge();

        const id = res?.payload?.challenge?._id;
        if (!id) throw new Error("No challenge assigned");

        const list = res?.payload?.challenges || [res?.payload?.challenge];
        setChallengesList(list);
        setChallengeId(id);
        setChallengeData(res?.payload?.challenge);
        await fetchData(id);
      } catch (e) {
        console.error(
          " Could not load judge challenge:",
          e?.response?.data || e.message,
        );
        setFetchError(
          e?.response?.data?.message ||
            "No challenge is currently assigned to you.",
        );
        setLoading(false);
      }
    };
    init();
  }, []);

  const fetchData = async (challengeId) => {
    if (!challengeId) {
      console.error(" challengeId is missing");
      return;
    }

    setLoading(true);

    try {
      // ✅ AB RANK DIRECT SUBMISSIONS API SE AAYEGA
      const res = await getSubmissionListById(challengeId, 1, 10);

      console.log(" API Response:", res);

      const data = res?.data || res;

      if (!data?.status) {
        console.error("API failed:", data?.message);
        setAllSubs([]);
        return;
      }

      const submissions = data?.payload?.submissions || [];

      // ✅ RANK AB SUBMISSIONS MEIN HI AA RAHA HAI
      const list = submissions.map((s, i) => ({
        id: s._id,
        team: s.user_id?.name || `Team ${i + 1}`,
        title: s.title || "Untitled",
        date: new Date(s.created_at).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        }),
        rawStatus: s.status || "pending",
        desc: s.description || "No description provided.",
        link: s.submit_link || "#",
        file: s.file_url,
        scores: s.scores || {},
        rank: s.rank || null, // ✅ DIRECTLY FROM API
        prize: s.prize || null, // ✅ DIRECTLY FROM API
      }));

      console.log("Submissions with ranks:", list);

      setAllSubs(list);

      if (list.length) setActiveId(list[0].id);

      const rev = list.filter((s) => isReviewed(s)).length;

      setStats({
        total: list.length,
        reviewed: rev,
        pending: list.length - rev,
      });
    } catch (e) {
      console.error(" Fetch error:", e?.response?.data || e.message);
    } finally {
      setLoading(false);
    }
  };

  /* ── derived / filtered list ── */
  const filtered = useMemo(() => {
    let list = [...allSubs];
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (s) =>
          s.team.toLowerCase().includes(q) || s.title.toLowerCase().includes(q),
      );
    }
    if (statusFilter) {
      if (statusFilter === "reviewed") list = list.filter((s) => isReviewed(s));
      else if (statusFilter === "pending")
        list = list.filter((s) => !isReviewed(s));
    }
    return list;
  }, [allSubs, search, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / ITEMS_PER_PAGE));
  const paginated = filtered.slice(
    (page - 1) * ITEMS_PER_PAGE,
    page * ITEMS_PER_PAGE,
  );

  // reset page when filter changes
  useEffect(() => {
    setPage(1);
  }, [search, statusFilter]);

  /* ── modal helpers ── */
  const openModal = (sub) => {
    setSelected(sub);
    setScores({ innovation: 0, technical: 0, uiux: 0, impact: 0 });
    setComments("");
    setSubmitError("");
    setModal(true);
  };

  const calcFinal = () => {
    const w = { innovation: 0.25, technical: 0.3, uiux: 0.2, impact: 0.25 };
    return Object.keys(scores)
      .reduce((s, k) => s + scores[k] * w[k], 0)
      .toFixed(1);
  };

  const handleSubmit = async () => {
    const finalScore = parseFloat(calcFinal());
    if (finalScore === 0.0) {
      setSubmitError("Please enter a score before submitting.");
      return;
    }

    setSubmitError("");
    setSubmitting(true);
    try {
      await submitJudgeScores({
        submissionId: selected.id,
        scores,
        finalScore: finalScore,
        comments,
        status: "reviewed",
      });
      setAllSubs((p) =>
        p.map((s) =>
          s.id === selected.id
            ? {
                ...s,
                rawStatus: "reviewed",
                scores: { ...scores, final: finalScore },
              }
            : s,
        ),
      );
      setStats((p) => ({
        ...p,
        reviewed: p.reviewed + 1,
        pending: Math.max(0, p.pending - 1),
      }));
      setModal(false);
      setToast(true);
      setTimeout(() => setToast(false), 3500);
    } catch (e) {
      console.error(e);
      setSubmitError(
        e?.response?.data?.message ||
          "Failed to submit score. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const active = allSubs.find((s) => s.id === activeId);

  /* ── loading ── */
  if (loading)
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#f1f5f9",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <Spinner
            style={{ width: "3rem", height: "3rem", color: "#6366f1" }}
          />
          <p style={{ marginTop: 16, color: "#64748b", fontWeight: 600 }}>
            Loading submissions…
          </p>
        </div>
      </div>
    );

  /* ── error: no challenge assigned ── */
  if (fetchError)
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#f1f5f9",
        }}
      >
        <div
          style={{
            textAlign: "center",
            background: "#fff",
            borderRadius: 20,
            padding: "48px 40px",
            boxShadow: "0 2px 24px rgba(0,0,0,0.09)",
          }}
        >
          <i
            className="fas fa-exclamation-circle"
            style={{
              fontSize: "3rem",
              color: "#f59e0b",
              display: "block",
              marginBottom: 16,
            }}
          ></i>
          <h5 style={{ color: "#0f172a", fontWeight: 700, marginBottom: 8 }}>
            No Challenge Assigned
          </h5>
          <p style={{ color: "#64748b", fontSize: "0.9rem", margin: 0 }}>
            {fetchError}
          </p>
        </div>
      </div>
    );

  /* ─── render ─────────────────────────────────────────────── */
  return (
    <>
      <style>{`
            @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
            .jd-root { font-family:'Inter',sans-serif; }
            .jd-root * { box-sizing:border-box; }

            .jd-hero {
                background:linear-gradient(135deg,#6366f1 0%,#8b5cf6 50%,#a78bfa 100%);
                padding:36px 32px 72px; position:relative; overflow:hidden;
            }
            .jd-hero::before {
                content:""; position:absolute; inset:0;
                background:url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none'%3E%3Cg fill='%23ffffff' fill-opacity='0.06'%3E%3Ccircle cx='30' cy='30' r='20'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
            }

            .jd-stat-card {
                background:#fff; border-radius:20px; box-shadow:0 2px 20px rgba(0,0,0,0.07);
                padding:22px 24px; margin-top:40px;
                transition:transform .2s,box-shadow .2s;
                height: 100%;
  display: flex;
  flex-direction: column;
            }
.jd-stat-card > div:first-child {
  flex-grow: 1;
}
            .jd-stat-card:hover { transform:translateY(-4px); box-shadow:0 8px 30px rgba(0,0,0,0.12); }

            .jd-icon-box {
                width:46px; height:46px; border-radius:13px;
                display:flex; align-items:center; justify-content:center; font-size:1.15rem; flex-shrink:0;
            }

            .jd-table-wrap { background:#fff; border-radius:20px; box-shadow:0 2px 20px rgba(0,0,0,0.07); overflow:hidden; }
            .jd-table { width:100%; border-collapse:collapse; }
            .jd-table thead th {
                background:#f8fafc; border:none; color:#64748b;
                font-size:0.63rem; font-weight:700; text-transform:uppercase; letter-spacing:1px; padding:13px 16px;
            }
            .jd-table tbody tr { cursor:pointer; transition:background .15s; border-left:3px solid transparent; }
            .jd-table tbody tr:hover:not(.jd-empty-row) { background:#f8fafc; }
            .jd-table tbody tr.jd-active { background:#f5f3ff; border-left-color:#6366f1; cursor:default; }
            .jd-table tbody tr.jd-empty-row { cursor:default; }
            .jd-table tbody td { padding:13px 16px; border-bottom:1px solid #f1f5f9; vertical-align:middle; color:#374151; font-size:0.85rem; }

            .jd-btn-primary {
                background:linear-gradient(135deg,#6366f1,#8b5cf6); border:none; border-radius:10px;
                color:#fff; font-weight:700; font-size:0.78rem; padding:7px 16px; cursor:pointer;
                transition:opacity .2s,transform .15s;
            }
            .jd-btn-primary:hover:not(:disabled) { opacity:.88; transform:scale(1.03); }
            .jd-btn-primary:disabled { opacity:.5; cursor:default; }

            .jd-btn-ghost {
                background:#f1f5f9; border:none; border-radius:10px;
                color:#64748b; font-weight:600; font-size:0.78rem; padding:7px 16px; cursor:pointer;
                transition:background .15s;
            }
            .jd-btn-ghost:disabled { opacity:.5; cursor:default; }
            .jd-btn-ghost:hover:not(:disabled) { background:#e2e8f0; }

            .jd-search-wrap { position:relative; flex:1; min-width:0; }
            .jd-search-wrap i { position:absolute; left:12px; top:50%; transform:translateY(-50%); color:#94a3b8; font-size:0.8rem; pointer-events:none; }
            .jd-search-input {
                width:100%; border:1.5px solid #e2e8f0; border-radius:10px;
                padding:9px 12px 9px 34px; font-size:0.83rem; color:#374151; outline:none;
                transition:border-color .2s;
            }
            .jd-search-input:focus { border-color:#6366f1; }
            .jd-filter-select {
                border:1.5px solid #e2e8f0; border-radius:10px; padding:9px 14px;
                font-size:0.83rem; color:#374151; background:#fff; outline:none; cursor:pointer;
                transition:border-color .2s; min-width:140px; max-width:280px; text-overflow:ellipsis; overflow:hidden; white-space:nowrap;
            }
            .jd-filter-select:focus { border-color:#6366f1; }

            .jd-pager { display:flex; align-items:center; gap:6px; flex-wrap:wrap; justify-content:center; padding:16px; }
            .jd-page-btn {
                width:34px; height:34px; border-radius:9px; border:1.5px solid #e2e8f0;
                background:#fff; color:#374151; font-size:0.82rem; font-weight:600; cursor:pointer;
                display:flex; align-items:center; justify-content:center; transition:all .15s;
            }
            .jd-page-btn:hover:not(:disabled) { border-color:#6366f1; color:#6366f1; background:#f5f3ff; }
            .jd-page-btn.jd-active-page { background:#6366f1; color:#fff; border-color:#6366f1; }
            .jd-page-btn:disabled { opacity:.4; cursor:default; }

            .jd-sidebar-card { background:#fff; border-radius:20px; box-shadow:0 2px 24px rgba(0,0,0,0.09); overflow:hidden; position:sticky; top:24px; }
            .jd-sidebar-head { background:linear-gradient(135deg,#6366f1,#8b5cf6); padding:22px; }
            .jd-sidebar-body { padding:22px; }

            .jd-link-btn {
                flex:1; border:1.5px solid #e2e8f0; border-radius:10px; background:#fff;
                color:#475569; font-size:0.8rem; font-weight:600; padding:9px 0; cursor:pointer;
                transition:all .2s; display:flex; align-items:center; justify-content:center; gap:7px; text-decoration:none;
            }
            .jd-link-btn:hover { border-color:#6366f1; color:#6366f1; background:#f5f3ff; text-decoration:none; }

            .jd-range { appearance:none; -webkit-appearance:none; width:100%; height:5px; border-radius:99px; outline:none; cursor:pointer; }
            .jd-range::-webkit-slider-thumb {
                -webkit-appearance:none; width:16px; height:16px; border-radius:50%;
                background:#6366f1; cursor:pointer; box-shadow:0 0 0 3px rgba(99,102,241,0.2); transition:transform .15s;
            }
            .jd-range::-webkit-slider-thumb:hover { transform:scale(1.2); }

            .jd-toast {
                position:fixed; top:24px; right:24px; z-index:9999;
                background:#fff; border-radius:14px; padding:16px 20px;
                box-shadow:0 8px 30px rgba(0,0,0,0.15); display:flex; align-items:center; gap:12px;
                min-width:290px; animation:jd-slide .3s ease;
            }
            @keyframes jd-slide { from{transform:translateX(40px);opacity:0} to{transform:translateX(0);opacity:1} }

            .jd-thumb {
                flex:1; height:108px; border-radius:14px; overflow:hidden; position:relative;
                transition:transform .2s,box-shadow .2s;
            }
            .jd-thumb:hover { transform:scale(1.03); box-shadow:0 8px 24px rgba(0,0,0,0.18); }
            .jd-thumb-overlay {
                position:absolute; inset:0; background:rgba(0,0,0,0.32);
                display:flex; align-items:center; justify-content:center; opacity:0; transition:opacity .2s;
            }
            .jd-thumb:hover .jd-thumb-overlay { opacity:1; }

            .jd-empty { text-align:center; padding:56px 0; color:#94a3b8; }
            .jd-empty i { font-size:2.4rem; display:block; margin-bottom:12px; }
        `}</style>

      <div
        className="jd-root"
        style={{ background: "#f1f5f9", minHeight: "100vh" }}
      >
        {/* ── Toast ── */}
        {toast && (
          <div className="jd-toast">
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 10,
                background: "#d1fae5",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <i className="fas fa-check" style={{ color: "#059669" }}></i>
            </div>
            <div>
              <p
                style={{
                  margin: 0,
                  fontWeight: 700,
                  color: "#0f172a",
                  fontSize: "0.88rem",
                }}
              >
                Scores submitted!
              </p>
              <p style={{ margin: 0, color: "#64748b", fontSize: "0.78rem" }}>
                {selected?.team} has been reviewed.
              </p>
            </div>
          </div>
        )}

        {/* ── Hero gradient bar ── */}
        <div
          className="jd-hero"
          style={{ padding: "30px 32px 70px", color: "#fff" }}
        >
          {challengesList.find((c) => c._id === challengeId) && (
            <div style={{ position: "relative", zIndex: 2 }}>
              <h2
                style={{
                  fontWeight: 800,
                  color: "#fff",
                  margin: 0,
                  fontSize: "1.8rem",
                }}
              >
                {challengesList.find((c) => c._id === challengeId).c_name}
              </h2>
              <p
                style={{
                  margin: "6px 0 0",
                  opacity: 0.9,
                  fontSize: "0.9rem",
                  fontWeight: 500,
                }}
              >
                Status:{" "}
                <span
                  style={{
                    background: "rgba(255,255,255,0.2)",
                    padding: "2px 8px",
                    borderRadius: 4,
                    textTransform: "uppercase",
                    fontSize: "0.8rem",
                  }}
                >
                  {challengesList.find((c) => c._id === challengeId).c_status}
                </span>
                {challengesList.find((c) => c._id === challengeId)
                  .c_description &&
                  ` · ${challengesList.find((c) => c._id === challengeId).c_description}`}
              </p>
            </div>
          )}
        </div>

        <Container fluid style={{ paddingBottom: 48 }}>
          {/* ── Stat cards ── */}
          <Row className="mb-5" style={{ alignItems: "flex-end" }}>
            {[
              {
                label: "Total Submissions",
                value: stats.total,
                iconClass: "fas fa-layer-group",
                iconBg: "#ede9fe",
                iconColor: "#6366f1",
                bar: null,
              },
              {
                label: "Reviewed",
                value: stats.reviewed,
                iconClass: "fas fa-check-circle",
                iconBg: "#d1fae5",
                iconColor: "#059669",
                barVal:
                  stats.total > 0 ? (stats.reviewed / stats.total) * 100 : 0,
                barColor: "#059669",
              },
              {
                label: "Pending",
                value: stats.pending,
                iconClass: "fas fa-hourglass-half",
                iconBg: "#fef3c7",
                iconColor: "#d97706",
                barVal:
                  stats.total > 0 ? (stats.pending / stats.total) * 100 : 0,
                barColor: "#f59e0b",
              },
            ].map((s, i) => (
              <Col lg="4" className="mb-3 mb-lg-0" key={i}>
                <div
                  className="jd-stat-card  d-flex flex-column"
                  style={{ height: "140px" }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      flex: 1,
                    }}
                  >
                    <div>
                      <p
                        style={{
                          fontSize: "0.67rem",
                          fontWeight: 700,
                          textTransform: "uppercase",
                          letterSpacing: "1px",
                          color: "#94a3b8",
                          marginBottom: 6,
                        }}
                      >
                        {s.label}
                      </p>
                      <p
                        style={{
                          fontSize: "2.1rem",
                          fontWeight: 800,
                          color: "#0f172a",
                          margin: 0,
                          lineHeight: 1,
                        }}
                      >
                        {s.value}
                      </p>
                    </div>
                    <div
                      className="jd-icon-box"
                      style={{ background: s.iconBg }}
                    >
                      <i
                        className={s.iconClass}
                        style={{ color: s.iconColor }}
                      ></i>
                    </div>
                  </div>
                  {s.barVal !== undefined && (
                    <div style={{ marginTop: "auto", paddingTop: 14 }}>
                      <div
                        style={{
                          height: 5,
                          background: "#f1f5f9",
                          borderRadius: 99,
                          overflow: "hidden",
                        }}
                      >
                        <div
                          style={{
                            width: `${s.barVal}%`,
                            height: "100%",
                            background: s.barColor,
                            borderRadius: 99,
                            transition: "width .5s ease",
                          }}
                        ></div>
                      </div>
                      <p
                        style={{
                          fontSize: "0.72rem",
                          color: "#94a3b8",
                          marginTop: 5,
                          marginBottom: 0,
                        }}
                      >
                        {s.barVal.toFixed(0)}% of total
                      </p>
                    </div>
                  )}
                </div>
              </Col>
            ))}
          </Row>

          <Row>
            {/* ── Table column ── */}
            <Col xl="8" className="mb-5 mb-xl-0">
              <div className="jd-table-wrap">
                {/* header + search + filter */}
                <div style={{ padding: "20px 20px 0" }}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      marginBottom: 16,
                    }}
                  >
                    <div>
                      <p
                        style={{
                          margin: 0,
                          fontWeight: 800,
                          fontSize: "1rem",
                          color: "#0f172a",
                        }}
                      >
                        <i
                          className="fas fa-list-ul"
                          style={{ color: "#6366f1", marginRight: 9 }}
                        ></i>
                        Active Submissions
                      </p>
                      <p
                        style={{
                          margin: 0,
                          fontSize: "0.75rem",
                          color: "#94a3b8",
                          marginTop: 3,
                        }}
                      >
                        {filtered.length} result
                        {filtered.length !== 1 ? "s" : ""} · page {page} of{" "}
                        {totalPages}
                      </p>
                    </div>
                    <button
                      onClick={() => fetchData(challengeId)}
                      style={{
                        background: "#f5f3ff",
                        border: "none",
                        borderRadius: 10,
                        padding: "8px 14px",
                        color: "#6366f1",
                        fontWeight: 600,
                        fontSize: "0.78rem",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                      }}
                    >
                      <i className="fas fa-sync-alt"></i> Refresh
                    </button>
                  </div>

                  {/* Search + filter row */}
                  <div
                    style={{
                      display: "flex",
                      gap: 10,
                      marginBottom: 16,
                      flexWrap: "wrap",
                    }}
                  >
                    <div className="jd-search-wrap" style={{ maxWidth: 240 }}>
                      <i className="fas fa-search"></i>
                      <input
                        className="jd-search-input"
                        placeholder="Search…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        style={{
                          padding: "7px 10px 7px 30px",
                          fontSize: "0.78rem",
                        }}
                      />
                    </div>
                    <select
                      className="jd-filter-select"
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                    >
                      <option value="">All Status</option>
                      <option value="reviewed">Reviewed</option>
                      <option value="pending">Pending</option>
                    </select>
                    {challengesList.length > 1 && (
                      <select
                        className="jd-filter-select"
                        value={challengeId || ""}
                        onChange={(e) => {
                          const val = e.target.value;
                          setChallengeId(val);
                          fetchData(val);
                        }}
                        style={{
                          minWidth: 200,
                          maxWidth: 280,
                          textOverflow: "ellipsis",
                          overflow: "hidden",
                        }}
                      >
                        {challengesList.map((c) => {
                          const label =
                            c.c_name.length > 45
                              ? c.c_name.substring(0, 45) + "..."
                              : c.c_name;
                          return (
                            <option key={c._id} value={c._id} title={c.c_name}>
                              {label} ({c.c_status})
                            </option>
                          );
                        })}
                      </select>
                    )}
                  </div>
                </div>

                {/* table */}
                <div style={{ overflowX: "auto" }}>
                  <table className="jd-table">
                    <thead>
                      <tr>
                        {[
                          "Rank",
                          "Team",
                          "Project Title",
                          "Date",
                          "Status",
                          "Action",
                        ].map((h) => (
                          <th key={h}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {paginated.length === 0 ? (
                        <tr className="jd-empty-row">
                          <td colSpan="6" style={{ padding: 0 }}>
                            <div className="jd-empty">
                              <i className="fas fa-inbox"></i>
                              No submissions match your filters
                            </div>
                          </td>
                        </tr>
                      ) : (
                        paginated.map((sub) => {
                          const isResultDeclared =
                            challengeData?.c_result_declared || false;
                          return (
                            <tr
                              key={sub.id}
                              className={activeId === sub.id ? "jd-active" : ""}
                              onClick={() => setActiveId(sub.id)}
                            >
                              {/* Rank Column */}
                              <td>
                                {sub.rank ? (
                                  <div
                                    style={{
                                      display: "inline-flex",
                                      alignItems: "center",
                                      gap: 6,
                                      padding: "4px 12px 4px 8px",
                                      borderRadius: 20,
                                      background:
                                        sub.rank === 1
                                          ? "linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)"
                                          : sub.rank === 2
                                            ? "linear-gradient(135deg, #d1d5db 0%, #9ca3af 100%)"
                                            : sub.rank === 3
                                              ? "linear-gradient(135deg, #fcd34d 0%, #d97706 100%)"
                                              : "#f1f5f9",
                                      color: sub.rank <= 3 ? "#fff" : "#64748b",
                                      fontWeight: 700,
                                      fontSize: "0.75rem",
                                      boxShadow:
                                        sub.rank <= 3
                                          ? "0 2px 8px rgba(0,0,0,0.15)"
                                          : "none",
                                    }}
                                  >
                                    <span
                                      style={{
                                        fontSize: "0.9rem",
                                        lineHeight: 1,
                                      }}
                                    >
                                      {sub.rank === 1
                                        ? "🥇"
                                        : sub.rank === 2
                                          ? "🥈"
                                          : sub.rank === 3
                                            ? "🥉"
                                            : "🏅"}
                                    </span>
                                    <span
                                      style={{
                                        fontSize: "0.7rem",
                                        fontWeight: 700,
                                        letterSpacing: "0.3px",
                                      }}
                                    >
                                      #{sub.rank}
                                    </span>
                                    {sub.prize && sub.rank <= 3 && (
                                      <span
                                        style={{
                                          fontSize: "0.6rem",
                                          opacity: 0.9,
                                          fontWeight: 600,
                                          marginLeft: 2,
                                        }}
                                      >
                                        {sub.prize}
                                      </span>
                                    )}
                                  </div>
                                ) : (
                                  <span
                                    style={{
                                      display: "inline-flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      width: 28,
                                      height: 28,
                                      borderRadius: "50%",
                                      background: "#f1f5f9",
                                      color: "#94a3b8",
                                      fontSize: "0.7rem",
                                      fontWeight: 600,
                                    }}
                                  >
                                    -
                                  </span>
                                )}
                              </td>

                              {/* Team Column */}
                              <td>
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 10,
                                  }}
                                >
                                  <div
                                    style={{
                                      width: 32,
                                      height: 32,
                                      borderRadius: 10,
                                      background: "#ede9fe",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      flexShrink: 0,
                                    }}
                                  >
                                    <i
                                      className="fas fa-users"
                                      style={{
                                        color: "#6366f1",
                                        fontSize: "0.7rem",
                                      }}
                                    ></i>
                                  </div>
                                  <span
                                    style={{
                                      fontWeight: 700,
                                      color: "#0f172a",
                                    }}
                                  >
                                    {sub.team}
                                  </span>
                                </div>
                              </td>

                              {/* Project Title Column */}
                              <td
                                style={{
                                  color: "#64748b",
                                  maxWidth: 160,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {sub.title}
                              </td>

                              {/* Date Column */}
                              <td
                                style={{
                                  color: "#94a3b8",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                <i
                                  className="far fa-calendar"
                                  style={{ marginRight: 5 }}
                                ></i>
                                {sub.date}
                              </td>

                              {/* Status Column */}
                              <td>
                                <Pill raw={sub.rawStatus} />
                              </td>

                              {/* Action Column */}
                              <td
                                onClick={(e) => e.stopPropagation()}
                                style={{ cursor: "default" }}
                              >
                                <button
                                  className="jd-btn-primary"
                                  disabled={isReviewed(sub) || isResultDeclared}
                                  onClick={() => openModal(sub)}
                                  style={
                                    isReviewed(sub) || isResultDeclared
                                      ? {
                                          background: "#f1f5f9",
                                          color: "#94a3b8",
                                          cursor: "default",
                                        }
                                      : { cursor: "pointer" }
                                  }
                                >
                                  <i
                                    className={`fas ${
                                      isReviewed(sub)
                                        ? "fa-check"
                                        : isResultDeclared
                                          ? "fa-trophy"
                                          : "fa-pen"
                                    }`}
                                    style={{ marginRight: 5 }}
                                  ></i>
                                  {isReviewed(sub)
                                    ? "Reviewed"
                                    : isResultDeclared
                                      ? "Results Declared"
                                      : "Score"}
                                </button>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>

                {/* pagination */}
                {totalPages > 1 && (
                  <div className="jd-pager">
                    <button
                      className="jd-page-btn"
                      disabled={page === 1}
                      onClick={() => setPage(1)}
                      title="First"
                    >
                      <i className="fas fa-angle-double-left"></i>
                    </button>
                    <button
                      className="jd-page-btn"
                      disabled={page === 1}
                      onClick={() => setPage((p) => p - 1)}
                      title="Prev"
                    >
                      <i className="fas fa-angle-left"></i>
                    </button>

                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                      .filter(
                        (n) =>
                          n === 1 ||
                          n === totalPages ||
                          Math.abs(n - page) <= 1,
                      )
                      .reduce((acc, n, idx, arr) => {
                        if (idx > 0 && n - arr[idx - 1] > 1) acc.push("…");
                        acc.push(n);
                        return acc;
                      }, [])
                      .map((n, i) =>
                        n === "…" ? (
                          <span
                            key={`e${i}`}
                            style={{
                              padding: "0 4px",
                              color: "#94a3b8",
                              fontSize: "0.85rem",
                            }}
                          >
                            …
                          </span>
                        ) : (
                          <button
                            key={n}
                            className={`jd-page-btn${n === page ? " jd-active-page" : ""}`}
                            onClick={() => setPage(n)}
                          >
                            {n}
                          </button>
                        ),
                      )}

                    <button
                      className="jd-page-btn"
                      disabled={page === totalPages}
                      onClick={() => setPage((p) => p + 1)}
                      title="Next"
                    >
                      <i className="fas fa-angle-right"></i>
                    </button>
                    <button
                      className="jd-page-btn"
                      disabled={page === totalPages}
                      onClick={() => setPage(totalPages)}
                      title="Last"
                    >
                      <i className="fas fa-angle-double-right"></i>
                    </button>
                  </div>
                )}
              </div>

              {/* thumbnails */}
              {allSubs.length > 0 && (
                <div style={{ display: "flex", gap: 14, marginTop: 20 }}>
                  {allSubs.slice(0, 3).map((sub, i) => {
                    const imgs = [
                      "1551288049-bebda4e38f71",
                      "1504868584819-f8e8b4b6d7e3",
                      "1518770660439-4636190af475",
                    ];
                    const bg = sub.file
                      ? `url(${sub.file})`
                      : `url('https://images.unsplash.com/photo-${imgs[i]}?auto=format&fit=crop&q=80&w=400')`;
                    return (
                      <div
                        key={i}
                        className="jd-thumb"
                        style={{
                          backgroundImage: bg,
                          backgroundSize: "cover",
                          backgroundPosition: "center",
                          cursor: activeId === sub.id ? "default" : "pointer",
                        }}
                        title={sub.title}
                        onClick={() => {
                          if (activeId !== sub.id) setActiveId(sub.id);
                        }}
                      >
                        <div className="jd-thumb-overlay">
                          <i
                            className="fas fa-expand-alt"
                            style={{ color: "#fff", fontSize: "1.1rem" }}
                          ></i>
                        </div>
                        <div
                          style={{
                            position: "absolute",
                            bottom: 0,
                            left: 0,
                            right: 0,
                            background:
                              "linear-gradient(to top,rgba(0,0,0,0.68),transparent)",
                            padding: "12px 10px 8px",
                            borderRadius: "0 0 14px 14px",
                          }}
                        >
                          <p
                            style={{
                              margin: 0,
                              color: "#fff",
                              fontSize: "0.7rem",
                              fontWeight: 600,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {sub.team}
                          </p>
                          <p
                            style={{
                              margin: 0,
                              color: "rgba(255,255,255,0.75)",
                              fontSize: "0.65rem",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {sub.title}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </Col>

            {/* ── Sidebar ── */}
            <Col xl="4">
              {active ? (
                <div className="jd-sidebar-card">
                  <div className="jd-sidebar-head">
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: 12,
                      }}
                    >
                      <span
                        style={{
                          fontSize: "0.64rem",
                          fontWeight: 700,
                          textTransform: "uppercase",
                          letterSpacing: "1.2px",
                          color: "rgba(255,255,255,0.72)",
                        }}
                      >
                        <i
                          className="fas fa-star"
                          style={{ marginRight: 5 }}
                        ></i>
                        Current Review
                      </span>
                      <div style={{ display: "flex", gap: 5 }}>
                        {[...Array(3)].map((_, i) => (
                          <div
                            key={i}
                            style={{
                              width: 5,
                              height: 5,
                              borderRadius: "50%",
                              background: "rgba(255,255,255,0.5)",
                            }}
                          ></div>
                        ))}
                      </div>
                    </div>
                    <h3
                      style={{
                        fontWeight: 800,
                        color: "#fff",
                        marginBottom: 3,
                        fontSize: "1.15rem",
                      }}
                    >
                      {active.team}
                    </h3>
                    <p
                      style={{
                        color: "rgba(255,255,255,0.78)",
                        margin: 0,
                        fontSize: "0.83rem",
                      }}
                    >
                      {active.title}
                    </p>

                    {active.rank && (
                      <div
                        style={{
                          marginTop: 14,
                          background: "rgba(255,255,255,0.15)",
                          borderRadius: 11,
                          padding: "9px 14px",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <span
                          style={{
                            color: "rgba(255,255,255,0.82)",
                            fontSize: "0.74rem",
                            fontWeight: 600,
                          }}
                        >
                          <i
                            className="fas fa-trophy"
                            style={{ marginRight: 6 }}
                          ></i>
                          Rank
                        </span>
                        <span
                          style={{
                            color: "#fff",
                            fontWeight: 800,
                            fontSize: "1.1rem",
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                          }}
                        >
                          {active.rank === 1
                            ? "🥇"
                            : active.rank === 2
                              ? "🥈"
                              : active.rank === 3
                                ? "�"
                                : `#${active.rank}`}
                          {active.prize && (
                            <span style={{ fontSize: "0.75rem", opacity: 0.8 }}>
                              ({active.prize})
                            </span>
                          )}
                        </span>
                      </div>
                    )}

                    {isReviewed(active) && active.scores?.final > 0 && (
                      <div
                        style={{
                          marginTop: 14,
                          background: "rgba(255,255,255,0.15)",
                          borderRadius: 11,
                          padding: "9px 14px",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <span
                          style={{
                            color: "rgba(255,255,255,0.82)",
                            fontSize: "0.74rem",
                            fontWeight: 600,
                          }}
                        >
                          Final Score
                        </span>
                        <span
                          style={{
                            color: "#fff",
                            fontWeight: 800,
                            fontSize: "1.1rem",
                          }}
                        >
                          {active.scores.final}/10
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="jd-sidebar-body">
                    <p
                      style={{
                        color: "#64748b",
                        fontSize: "0.84rem",
                        lineHeight: 1.72,
                        marginBottom: 18,
                      }}
                    >
                      {active.desc}
                    </p>

                    {/* <div style={{ display: "flex", gap: 10, marginBottom: 22 }}>
                                            <a href={active.link} target="_blank" rel="noopener noreferrer" className="jd-link-btn">
                                                <i className="fab fa-github"></i> GitHub
                                            </a>
                                            <button className="jd-link-btn" style={{ background: "#f8fafc", borderColor: "#f1f5f9", color: "#94a3b8", cursor: "default" }}>
                                                <i className="fas fa-external-link-alt"></i> Live Demo
                                            </button>
                                        </div> */}

                    {/* Score sliders — show for all, read-only for reviewed */}
                    <div style={{ marginBottom: 20 }}>
                      {[
                        { key: "innovation", label: "Innovation" },
                        { key: "technical", label: "Technical Feasibility" },
                        { key: "uiux", label: "UI/UX" },
                        { key: "impact", label: "Potential Impact" },
                      ].map((c) => {
                        const val = active.scores?.[c.key] ?? 0;
                        const pct = val * 10;
                        return (
                          <div key={c.key} style={{ marginBottom: 14 }}>
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                marginBottom: 5,
                              }}
                            >
                              <span
                                style={{
                                  fontWeight: 600,
                                  color: "#374151",
                                  fontSize: "0.82rem",
                                }}
                              >
                                {c.label}
                              </span>
                              <span
                                style={{
                                  fontWeight: 700,
                                  color: "#6366f1",
                                  fontSize: "0.82rem",
                                }}
                              >
                                {val}/10
                              </span>
                            </div>
                            <div
                              style={{
                                height: 5,
                                background: "#e2e8f0",
                                borderRadius: 99,
                                overflow: "hidden",
                              }}
                            >
                              <div
                                style={{
                                  width: `${pct}%`,
                                  height: "100%",
                                  background:
                                    "linear-gradient(135deg,#6366f1,#8b5cf6)",
                                  borderRadius: 99,
                                  transition: "width 0.4s ease",
                                }}
                              ></div>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Final score box if reviewed */}
                    {isReviewed(active) && active.scores?.final > 0 && (
                      <div
                        style={{
                          background: "linear-gradient(135deg,#6366f1,#8b5cf6)",
                          borderRadius: 13,
                          padding: "14px 18px",
                          marginBottom: 18,
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <div>
                          <p
                            style={{
                              margin: 0,
                              fontSize: "0.62rem",
                              fontWeight: 700,
                              textTransform: "uppercase",
                              letterSpacing: "1px",
                              color: "rgba(255,255,255,0.72)",
                            }}
                          >
                            <i
                              className="fas fa-trophy"
                              style={{ marginRight: 5 }}
                            ></i>
                            Final Score
                          </p>
                          <p
                            style={{
                              margin: 0,
                              color: "rgba(255,255,255,0.72)",
                              fontSize: "0.73rem",
                            }}
                          >
                            Weighted average
                          </p>
                        </div>
                        <div style={{ textAlign: "center" }}>
                          <span
                            style={{
                              fontSize: "1.8rem",
                              fontWeight: 800,
                              color: "#fff",
                              lineHeight: 1,
                            }}
                          >
                            {active.scores.final}
                          </span>
                          <span
                            style={{
                              color: "rgba(255,255,255,0.7)",
                              fontSize: "0.75rem",
                            }}
                          >
                            /10
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Submit button — or static reviewed label */}
                    {isReviewed(active) ? (
                      <div
                        style={{
                          width: "100%",
                          padding: "13px",
                          borderRadius: 13,
                          background: "#d1fae5",
                          color: "#065f46",
                          fontWeight: 700,
                          fontSize: "0.88rem",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 8,
                          pointerEvents: "none",
                          userSelect: "none",
                        }}
                      >
                        <i className="fas fa-check-circle"></i>
                        Reviewed
                      </div>
                    ) : challengeData?.c_result_declared ? (
                      <div
                        style={{
                          width: "100%",
                          padding: "13px",
                          borderRadius: 13,
                          background: "#e0e7ff",
                          color: "#3730a3",
                          fontWeight: 700,
                          fontSize: "0.88rem",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 8,
                          pointerEvents: "none",
                          userSelect: "none",
                        }}
                      >
                        <i className="fas fa-trophy"></i>
                        Results Declared
                      </div>
                    ) : (
                      <button
                        onClick={() => openModal(active)}
                        style={{
                          width: "100%",
                          padding: "13px",
                          border: "none",
                          borderRadius: 13,
                          cursor: "pointer",
                          background: "linear-gradient(135deg,#6366f1,#8b5cf6)",
                          color: "#fff",
                          fontWeight: 700,
                          fontSize: "0.88rem",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 8,
                          transition: "opacity .2s",
                        }}
                      >
                        <i className="fas fa-paper-plane"></i>
                        Submit Scores
                      </button>
                    )}

                    <div
                      style={{
                        marginTop: 18,
                        background: "#f8fafc",
                        borderRadius: 13,
                        padding: "13px 15px",
                        borderLeft: "3px solid #6366f1",
                      }}
                    >
                      <p
                        style={{
                          fontWeight: 700,
                          color: "#0f172a",
                          fontSize: "0.81rem",
                          marginBottom: 4,
                        }}
                      >
                        <i
                          className="fas fa-lightbulb"
                          style={{ color: "#6366f1", marginRight: 6 }}
                        ></i>
                        Judging Tip
                      </p>
                      <p
                        style={{
                          color: "#64748b",
                          fontSize: "0.77rem",
                          margin: 0,
                          lineHeight: 1.65,
                        }}
                      >
                        Prioritize functional MVPs. Technical Feasibility
                        carries the highest weight (30%).
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div
                  style={{
                    background: "#fff",
                    borderRadius: 20,
                    padding: 40,
                    textAlign: "center",
                    boxShadow: "0 2px 20px rgba(0,0,0,0.06)",
                  }}
                >
                  <i
                    className="fas fa-clipboard"
                    style={{
                      fontSize: "2.5rem",
                      color: "#cbd5e1",
                      marginBottom: 14,
                    }}
                  ></i>
                  {filtered.length === 0 ? (
                    <>
                      <h5 style={{ color: "#475569", fontWeight: 700 }}>
                        No Submissions
                      </h5>
                      <p
                        style={{
                          color: "#94a3b8",
                          fontSize: "0.85rem",
                          margin: 0,
                        }}
                      >
                        Adjust your filters to see more results.
                      </p>
                    </>
                  ) : (
                    <>
                      <h5 style={{ color: "#475569", fontWeight: 700 }}>
                        No Selection
                      </h5>
                      <p
                        style={{
                          color: "#94a3b8",
                          fontSize: "0.85rem",
                          margin: 0,
                        }}
                      >
                        Click a row in the table to view details.
                      </p>
                    </>
                  )}
                </div>
              )}
            </Col>
          </Row>
        </Container>

        {/* ── Score Modal ── */}
        <Modal
          isOpen={modal}
          toggle={() => setModal(false)}
          size="lg"
          backdrop="static"
          style={{ maxWidth: 720, marginTop: 40 }}
        >
          <ModalHeader
            toggle={() => setModal(false)}
            style={{
              background: "linear-gradient(135deg,#6366f1,#8b5cf6)",
              border: "none",
            }}
          >
            <div style={{ color: "#fff" }}>
              <p
                style={{
                  margin: 0,
                  fontSize: "0.67rem",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "1px",
                  color: "rgba(255,255,255,0.72)",
                  marginBottom: 4,
                }}
              >
                Judging Form
              </p>
              <span style={{ fontWeight: 800, fontSize: "1.05rem" }}>
                <i className="fas fa-star" style={{ marginRight: 8 }}></i>
                {selected?.team}
              </span>
              <p
                style={{
                  margin: 0,
                  fontSize: "0.8rem",
                  color: "rgba(255,255,255,0.78)",
                  marginTop: 2,
                }}
              >
                {selected?.title}
              </p>
            </div>
          </ModalHeader>

          <ModalBody
            style={{ padding: "24px 24px 8px", background: "#f8fafc" }}
          >
            <div
              style={{
                background: "#ede9fe",
                borderRadius: 11,
                padding: "11px 15px",
                marginBottom: 20,
                display: "flex",
                gap: 10,
                alignItems: "center",
              }}
            >
              <i
                className="fas fa-info-circle"
                style={{ color: "#6366f1", flexShrink: 0 }}
              ></i>
              <p
                style={{
                  margin: 0,
                  fontSize: "0.81rem",
                  color: "#4338ca",
                  fontWeight: 500,
                }}
              >
                Rate each criterion 0–10. Final score is calculated with
                weighted averages.
              </p>
            </div>

            {[
              {
                key: "innovation",
                label: "Innovation",
                weight: "25%",
                desc: "Originality & creativity",
              },
              {
                key: "technical",
                label: "Technical Depth",
                weight: "30%",
                desc: "Implementation & complexity",
              },
              {
                key: "uiux",
                label: "UI / UX",
                weight: "20%",
                desc: "User interface design",
              },
              {
                key: "impact",
                label: "Potential Impact",
                weight: "25%",
                desc: "Market potential & scale",
              },
            ].map((c) => {
              const v = scores[c.key];
              const pct = v * 10;
              return (
                <div
                  key={c.key}
                  style={{
                    background: "#f1f5f9",
                    borderRadius: 13,
                    padding: "15px 16px",
                    marginBottom: 12,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      marginBottom: 10,
                    }}
                  >
                    <div>
                      <p
                        style={{
                          margin: 0,
                          fontWeight: 700,
                          color: "#0f172a",
                          fontSize: "0.86rem",
                        }}
                      >
                        {c.label}
                      </p>
                      <p
                        style={{
                          margin: 0,
                          color: "#94a3b8",
                          fontSize: "0.73rem",
                        }}
                      >
                        {c.desc} · Weight {c.weight}
                      </p>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <span
                        style={{
                          fontSize: "1.55rem",
                          fontWeight: 800,
                          color: "#6366f1",
                          lineHeight: 1,
                        }}
                      >
                        {v}
                      </span>
                      <span style={{ fontSize: "0.73rem", color: "#94a3b8" }}>
                        /10
                      </span>
                    </div>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="10"
                    step="0.5"
                    value={v}
                    onChange={(e) =>
                      setScores((p) => ({
                        ...p,
                        [c.key]: parseFloat(e.target.value),
                      }))
                    }
                    className="jd-range"
                    style={{
                      background: `linear-gradient(to right,#6366f1 ${pct}%,#e2e8f0 ${pct}%)`,
                    }}
                  />
                </div>
              );
            })}

            <FormGroup style={{ marginTop: 8 }}>
              <Label
                style={{
                  fontWeight: 700,
                  fontSize: "0.86rem",
                  color: "#0f172a",
                }}
              >
                <i
                  className="fas fa-comment-alt"
                  style={{ color: "#6366f1", marginRight: 7 }}
                ></i>
                Feedback for Team
              </Label>
              <textarea
                rows="3"
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                placeholder="Share constructive feedback with the team…"
                style={{
                  width: "100%",
                  borderRadius: 12,
                  border: "1.5px solid #e2e8f0",
                  padding: "10px 14px",
                  fontSize: "0.84rem",
                  color: "#374151",
                  resize: "none",
                  outline: "none",
                  fontFamily: "inherit",
                }}
              />
            </FormGroup>

            <div
              style={{
                background: "linear-gradient(135deg,#6366f1,#8b5cf6)",
                borderRadius: 15,
                padding: "18px 22px",
                marginBottom: 8,
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <div>
                  <p
                    style={{
                      margin: 0,
                      fontSize: "0.65rem",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "1px",
                      color: "rgba(255,255,255,0.72)",
                      marginBottom: 3,
                    }}
                  >
                    <i className="fas fa-trophy" style={{ marginRight: 5 }}></i>
                    Final Score
                  </p>
                  <p
                    style={{
                      margin: 0,
                      color: "rgba(255,255,255,0.72)",
                      fontSize: "0.78rem",
                    }}
                  >
                    Weighted average
                  </p>
                </div>
                <div>
                  <span
                    style={{
                      fontSize: "3rem",
                      fontWeight: 800,
                      color: "#fff",
                      lineHeight: 1,
                    }}
                  >
                    {calcFinal()}
                  </span>
                  <span
                    style={{
                      color: "rgba(255,255,255,0.7)",
                      fontSize: "0.88rem",
                    }}
                  >
                    {" "}
                    / 10
                  </span>
                </div>
              </div>
            </div>

            {submitError && (
              <div
                style={{
                  marginTop: 15,
                  padding: "10px 14px",
                  background: "#fee2e2",
                  color: "#991b1b",
                  border: "1px solid #f87171",
                  borderRadius: 8,
                  fontSize: "0.85rem",
                  fontWeight: 600,
                }}
              >
                <i
                  className="fas fa-exclamation-circle"
                  style={{ marginRight: 6 }}
                ></i>
                {submitError}
              </div>
            )}
          </ModalBody>

          <ModalFooter
            style={{
              border: "none",
              background: "#f8fafc",
              padding: "12px 24px 20px",
              gap: 10,
            }}
          >
            <button
              onClick={() => setModal(false)}
              className="jd-btn-ghost"
              style={{ padding: "10px 22px", fontSize: "0.86rem" }}
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="jd-btn-primary"
              style={{
                padding: "10px 26px",
                fontSize: "0.86rem",
                display: "flex",
                alignItems: "center",
                gap: 8,
                opacity: submitting ? 0.75 : 1,
              }}
            >
              {submitting ? (
                <>
                  <Spinner size="sm" />
                  &nbsp;Submitting…
                </>
              ) : (
                <>
                  <i className="fas fa-paper-plane"></i> Submit Scores
                </>
              )}
            </button>
          </ModalFooter>
        </Modal>
      </div>
    </>
  );
};

export default JudgesDashboard;
